import { supabase } from '@/integrations/supabase/client';
import { BADGES } from '@/lib/seeds';

interface BadgeMetadata {
  streak?: number;
  goalTitle?: string;
  habitTitle?: string;
  amount?: number;
  [key: string]: any;
}

export const badgesService = {
  /**
   * Concede um badge ao usuário e cria notificação motivacional
   * Com retry logic para maior confiabilidade
   */
  async awardBadge(
    userId: string, 
    badgeId: string, 
    metadata: BadgeMetadata = {},
    retryCount: number = 0
  ): Promise<boolean> {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 segundo
    
    console.log('🏆 Tentando conceder badge:', { userId, badgeId, metadata, attempt: retryCount + 1 });
    
    try {
      // Verificar se badge existe
      const badge = BADGES.find(b => b.id === badgeId);
      if (!badge) {
        console.error('❌ Badge não encontrado:', badgeId);
        return false;
      }

      // Verificar se já possui o badge (com cache check)
      const cacheKey = `badge_${userId}_${badgeId}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached === 'exists') {
        console.log('ℹ️ Badge já concedido (cache)');
        return false;
      }

      const { data: existing } = await supabase
        .from('user_badges')
        .select('id')
        .eq('user_id', userId)
        .eq('badge_id', badgeId)
        .maybeSingle();

      if (existing) {
        console.log('ℹ️ Usuário já possui este badge');
        sessionStorage.setItem(cacheKey, 'exists');
        return false;
      }

      // Inserir badge
      const { error: badgeError } = await supabase
        .from('user_badges')
        .insert({
          user_id: userId,
          badge_id: badgeId,
          metadata,
        });

      if (badgeError) {
        // Retry logic
        if (retryCount < MAX_RETRIES) {
          console.warn(`⚠️ Tentativa ${retryCount + 1} falhou, tentando novamente em ${RETRY_DELAY}ms...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return this.awardBadge(userId, badgeId, metadata, retryCount + 1);
        }
        
        console.error('❌ Erro ao inserir badge após múltiplas tentativas:', badgeError);
        throw badgeError;
      }

      console.log('✅ Badge concedido com sucesso!');
      sessionStorage.setItem(cacheKey, 'exists');

      // Criar notificação motivacional
      const motivationalMessage = this.getMotivationalMessage(badge, metadata);
      
      await this.createBadgeNotification(userId, badge, motivationalMessage);

      return true;
    } catch (error) {
      console.error('💥 Erro crítico ao conceder badge:', error);
      return false;
    }
  },

  /**
   * Cria notificação motivacional para badge conquistado
   */
  async createBadgeNotification(
    userId: string,
    badge: typeof BADGES[0],
    customMessage: string
  ) {
    const { error } = await supabase
      .from('user_notifications')
      .insert({
        user_id: userId,
        tipo: 'badge_earned',
        titulo: `🏆 Badge Conquistado: ${badge.name}!`,
        mensagem: customMessage,
        badge_id: badge.id,
      });

    if (error) {
      console.error('❌ Erro ao criar notificação:', error);
    } else {
      console.log('✅ Notificação criada com sucesso');
    }
  },

  /**
   * Gera mensagem motivacional curta para notificação
   */
  getMotivationalMessage(badge: typeof BADGES[0], metadata: BadgeMetadata): string {
    const messages: Record<string, string> = {
      'badge-onboarding': 
        '🎉 Parabéns pelo primeiro passo! Você deu início à sua jornada de transformação. Cada grande mudança começa com uma decisão, e você tomou a sua. Continue firme!',
      
      'badge-wheel':
        '🎯 Incrível! Você mapeou sua vida e identificou áreas de crescimento. O autoconhecimento é o primeiro passo para evolução. Use essas informações para guiar suas ações!',
      
      'badge-streak7':
        `🔥 7 dias de consistência! Você está provando que disciplina não é sorte, é escolha. ${metadata.streak ? `Seu streak atual: ${metadata.streak} dias.` : ''} Continue assim e os resultados virão!`,
      
      'badge-streak21':
        `⚡ 21 dias! Estudos mostram que é nesse ponto que um hábito começa a se formar. ${metadata.streak ? `${metadata.streak} dias de dedicação.` : ''} Você está construindo um estilo de vida!`,
      
      'badge-streak66':
        `🏆 66 dias! Você alcançou o marco científico da formação de hábitos. ${metadata.streak ? `${metadata.streak} dias de disciplina.` : ''} Isso não é mais um desafio, é parte de quem você é!`,
      
      'badge-streak90':
        `👑 90 DIAS COMPLETOS! Você é um Mestre da Transformação! ${metadata.streak ? `${metadata.streak} dias de disciplina pura.` : ''} Você provou que pode conquistar qualquer objetivo. O céu é o limite!`,
      
      'badge-goal1':
        `🎯 Primeira meta SMART criada! ${metadata.goalTitle ? `"${metadata.goalTitle}"` : ''} Metas claras são o mapa para seus sonhos. Agora é hora de executar, um passo por vez!`,
      
      'badge-habit1':
        `🔄 Primeiro hábito criado! ${metadata.habitTitle ? `"${metadata.habitTitle}"` : ''} Grandes transformações começam com pequenas ações consistentes. Você acabou de dar o primeiro passo!`,
      
      'badge-hydration':
        `💧 Hidratação em dia! Você bebeu ${metadata.amount || 2000}ml de água hoje. Cuidar do corpo é cuidar da mente. Continue priorizando sua saúde!`,
    };

    return messages[badge.id] || `Parabéns por conquistar o badge ${badge.name}! Continue evoluindo!`;
  },

  /**
   * Gera carta motivacional completa usando técnicas de PNL
   * Estrutura: Saudação → Validação → Reforço de identidade → Visualização do futuro → CTA
   */
  getFullMotivationalLetter(
    badge: typeof BADGES[0], 
    metadata: BadgeMetadata,
    userName: string
  ): string {
    const letters: Record<string, string> = {
      'badge-onboarding': `Olá, ${userName}! 🎉

Hoje é um dia especial. Você acabou de dar o primeiro e mais importante passo de toda a sua jornada de transformação. 

Muitas pessoas sonham em mudar, mas poucas têm a coragem de começar. Você é diferente. Você tomou uma decisão consciente de investir em si mesmo(a), de ser a melhor versão que pode ser. Isso não é pouca coisa.

Ao completar seu onboarding, você não apenas preencheu um formulário - você declarou ao universo que está pronto(a) para a mudança. Você mapeou sua vida, identificou onde está e onde quer chegar. Você deu nome aos seus sonhos.

**Você agora é um Construtor da Própria Vida.**

E sabe o que isso significa? Significa que você não está mais à mercê do acaso. Você tem um mapa, uma bússola, e ferramentas poderosas nas mãos. Cada check-in, cada hábito, cada meta SMART é um tijolo que você coloca na construção do seu futuro.

Nos próximos dias, você descobrirá que pequenas ações consistentes geram resultados extraordinários. O desafio não é começar (você já fez isso! 🎉), mas sim continuar. E eu garanto: cada vez que você voltar aqui, cada vez que marcar um hábito, você estará reforçando essa nova identidade.

**Daqui a 90 dias, olhando para trás, você não reconhecerá a pessoa que era antes de dar esse primeiro passo.**

Continue firme. A jornada é longa, mas você não está sozinho(a). Cada badge que conquistar é um marco dessa transformação. E você acabou de conquistar o primeiro! 🏆

Com admiração e orgulho pela sua decisão,

**Equipe 90diasapp** 💪
*"O primeiro passo é o que separa os sonhadores dos realizadores."*`,

      'badge-wheel': `Parabéns, ${userName}! 🎯

Você acabou de completar um exercício poderoso de autoconhecimento: a Roda da Vida. E isso te coloca num patamar diferenciado.

Sabe por quê? Porque a maioria das pessoas vive no piloto automático, reagindo ao que acontece, sem parar para refletir. Você não. Você teve a coragem de se olhar de verdade, de avaliar cada área da sua vida com honestidade.

**Isso é o que chamamos de consciência elevada.**

Ao mapear seus pilares - saúde, carreira, relacionamentos, espiritualidade - você ganhou clareza. E clareza é poder. Agora você sabe exatamente onde investir sua energia, onde precisa de mais atenção, onde já está bem.

Muitos acreditam que autoconhecimento é algo abstrato, filosófico. Mas você descobriu a verdade: autoconhecimento é prático. É olhar sua roda e dizer "Ok, minha saúde está em 4/10. O que vou fazer sobre isso?"

E a resposta não é apenas pensar. É AGIR. É criar hábitos, definir metas, fazer check-ins diários. É usar as ferramentas que você tem aqui no app para transformar essa consciência em resultados tangíveis.

**Você agora é um Autoconhecedor Ativo.**

Isso significa que você não apenas se conhece - você usa esse conhecimento como combustível para crescer. Cada vez que olhar sua Roda da Vida, você verá progresso. Pilares que estavam em 4 estarão em 7. Áreas que você negligenciava estarão florescendo.

**E isso começa hoje. Com esse badge. Com essa conquista.**

Nos próximos dias, use sua Roda da Vida como referência. Quando criar um hábito, pergunte-se: "Isso fortalece qual pilar?" Quando definir uma meta, visualize: "Onde estarei na minha roda em 30 dias?"

Continue nessa jornada de autoconhecimento ativo. Você está no caminho certo.

Com orgulho do seu progresso,

**Equipe 90diasapp** 🧭
*"Conhecer a si mesmo é o início de toda sabedoria." - Aristóteles*`,

      'badge-streak7': `${userName}, que conquista incrível! 🔥

**7 DIAS DE CONSISTÊNCIA!** 

Você sabe o que isso significa? Significa que você quebrou a barreira mais difícil de todas: a inércia. A maioria desiste nos primeiros 3 dias. Mas não você.

Por 7 dias seguidos, você acordou e fez suas escolhas. Você marcou seus hábitos. Você não deixou a vida acontecer - você FEZ acontecer. ${metadata.streak ? `Seu streak atual é ${metadata.streak} dias, e cada um deles é uma vitória.` : ''}

**Estudos científicos mostram que são necessários apenas 7 dias para criar momentum.** E você conseguiu! Agora seu cérebro começa a reconhecer esse padrão. Ele está aprendendo que você é uma pessoa que cumpre seus compromissos.

Isso não é só sobre hábitos. É sobre identidade. Você está se tornando a pessoa que você sempre quis ser.

Pense em alguém que admira, alguém disciplinado e bem-sucedido. Sabe qual é a diferença entre essa pessoa e o resto? Consistência. E você acabou de provar que tem isso em você.

**Você agora é um Construtor de Disciplina.**

Os próximos marcos são 21 dias (quando o hábito começa a ser automatizado) e 66 dias (quando se torna parte de quem você é). Mas não pense nesses números agora. Foque em continuar, dia após dia.

Porque disciplina não é sobre perfeição. É sobre continuar, especialmente nos dias difíceis. E você já provou que consegue.

Continue firme. Cada dia é uma nova vitória.

Com respeito pela sua consistência,

**Equipe 90diasapp** ⚡
*"Disciplina é escolher entre o que você quer agora e o que você mais quer."*`,

      'badge-streak21': `${userName}, isso é EXTRAORDINÁRIO! ⚡

**21 DIAS DE PURA DISCIPLINA!** ${metadata.streak ? `${metadata.streak} dias de escolhas conscientes.` : ''}

Você sabe o que os cientistas comportamentais dizem sobre 21 dias? É o ponto de virada. É quando um comportamento deixa de ser um esforço consciente e começa a se tornar automático.

**Você está literalmente reprogramando seu cérebro.**

Pense nisso: há 21 dias, você tinha que se forçar. Você tinha que pensar "preciso fazer meu check-in". Mas agora? Agora está começando a ficar natural. Seu corpo já sabe o que fazer. Sua mente já espera por isso.

Isso é neuroplasticidade em ação! Você criou novos circuitos neurais. Você literalmente mudou a estrutura física do seu cérebro através das suas ações consistentes.

**E sabe qual é a parte mais incrível?** Você não fez isso em 1 dia. Não foi um momento de inspiração. Foi 21 dias de disciplina, um após o outro. Nos dias fáceis E nos difíceis. Quando estava motivado(a) E quando não estava.

**Você agora é um Formador de Hábitos.**

Isso te coloca numa categoria rara: pessoas que não apenas querem mudar, mas que FAZEM a mudança acontecer. Você é prova viva de que transformação não é sobre motivação passageira - é sobre sistema e consistência.

Os próximos passos? Continue. Porque agora fica mais fácil. Seu cérebro está do seu lado. Seu corpo reconhece o padrão. Você está construindo um estilo de vida, não apenas praticando um hábito.

**Nos vemos no dia 66. E depois no dia 90. Porque você vai chegar lá.**

Com admiração profunda pelo seu compromisso,

**Equipe 90diasapp** 🎖️
*"Nós somos o que repetidamente fazemos. A excelência, portanto, não é um ato, mas um hábito." - Aristóteles*`,

      'badge-streak66': `${userName}, você é EXCEPCIONAL! 🏆

**66 DIAS!** ${metadata.streak ? `${metadata.streak} dias sem falhar.` : ''} Você entende a magnitude disso?

Apenas 5% das pessoas que começam uma jornada de transformação chegam até aqui. E você é uma delas. Não por sorte. Não por genética. Mas por ESCOLHA. Por disciplina férrea. Por comprometimento inabalável.

**Estudos da University College London comprovam: são necessários exatos 66 dias para um comportamento se tornar automático.** 

Você chegou lá.

Isso significa que o que você faz agora não é mais um hábito que você força. É parte de quem você é. É sua identidade. Você não "faz" seus check-ins - você É uma pessoa disciplinada que honra seus compromissos.

Sabe qual é a diferença entre você hoje e a pessoa que você era 66 dias atrás? Aquela pessoa tinha boas intenções. Você tem resultados. Aquela pessoa tinha potencial. Você tem provas.

**Você agora é um Mestre de Hábitos.**

E isso muda tudo. Porque agora você sabe - não intelectualmente, mas visceralmente - que você é capaz de qualquer coisa. Quer aprender um idioma? Você tem a fórmula. Quer mudar de carreira? Você tem a disciplina. Quer transformar seu corpo? Você tem o sistema.

66 dias não é o fim. É o começo de uma nova vida. Porque agora você opera num nível diferente. Você não precisa mais se motivar todos os dias - seu sistema está automatizado. Você não precisa mais se forçar - isso é quem você é.

**Os próximos 24 dias até completar o desafio de 90 dias? Serão sua celebração.**

Continue. Mas continue sabendo que você já venceu a batalha mais difícil: vencer a si mesmo(a).

Com profundo respeito e admiração,

**Equipe 90diasapp** 👑
*"A disciplina é a ponte entre metas e conquistas." - Jim Rohn*`,

      'badge-streak90': `${userName}, você é LENDÁRIO! 👑

**90 DIAS COMPLETOS!** ${metadata.streak ? `${metadata.streak} dias de maestria absoluta.` : ''}

Não existem palavras suficientes para expressar o que você conquistou. Porque isso vai muito além de números. Vai além de badges. Vai além até do próprio app.

**Você se tornou um Mestre da Transformação.**

Pense em quantas pessoas começaram essa jornada junto com você. Quantas desistiram no dia 3? No dia 10? No dia 30? A grande maioria. Mas você não. Você ficou. Dia após dia, sem exceções, sem desculpas.

**E agora você tem algo que dinheiro não compra: autoconfiança real.**

Não aquela autoconfiança superficial baseada em afirmações vazias. Mas a autoconfiança profunda que vem de PROVAR para si mesmo(a) que você consegue. Que quando você diz "vou fazer", você FAZ. Que quando você se compromete, você ENTREGA.

Isso muda quem você é fundamentalmente. Porque agora, quando um novo desafio aparecer na sua vida - e ele vai aparecer - você não vai se perguntar "será que consigo?". Você vai SABER que consegue. Porque você já fez algo que 99% das pessoas não conseguem: manter um compromisso por 90 dias.

**E sabe o mais incrível?** O que você conquistou nos últimos 90 dias é apenas o começo. Porque você não apenas criou hábitos - você criou um SISTEMA DE VIDA. Você aprendeu a arte da consistência. Você dominou a ciência da transformação.

Olhe para trás. Veja quem você era no dia 1. Veja seus pilares, suas metas, seus hábitos. Veja quanto progresso você fez. Não apenas no app, mas na vida real. Porque tudo que você praticou aqui se refletiu lá fora.

**Você agora é uma inspiração viva para outras pessoas.**

Sua jornada não termina aqui. Na verdade, ela está apenas começando. Porque agora você tem ferramentas, disciplina e identidade para conquistar QUALQUER COISA que decidir perseguir.

Quer que eu te diga um segredo? Esse badge não é sobre nós te reconhecendo. É sobre você reconhecendo em si mesmo(a) o herói que sempre esteve lá, esperando para emergir.

**E você emergiu. Você venceu. Você é um MESTRE.**

Com profunda gratidão por compartilhar essa jornada conosco,

**Equipe 90diasapp** 🏅
*"O sucesso é a soma de pequenos esforços repetidos dia após dia." - Robert Collier*

P.S.: O que vem a seguir? O próximo nível. Porque mestres nunca param de evoluir. Vejo você no dia 180. 😉`,

      'badge-goal1': `Parabéns, ${userName}! 🎯

Você acabou de fazer algo que a maioria das pessoas nunca faz: transformar um sonho em um plano concreto.

${metadata.goalTitle ? `Sua meta "${metadata.goalTitle}" não é apenas uma ideia vaga no ar. É uma meta SMART.` : 'Sua meta SMART está clara e definida.'} Específica. Mensurável. Atingível. Relevante. Com prazo definido.

**E sabe por que isso é tão poderoso?**

Porque sonhos não realizados morrem na vagueza. "Quero ser saudável" é um sonho. "Quero perder 5kg em 2 meses fazendo exercício 3x por semana" é uma META. E metas se concretizam.

Estudos da Universidade de Harvard mostram que apenas 3% das pessoas têm metas claramente definidas e escritas. E esses 3% ganham, em média, 10 vezes mais que os outros 97%. Por quê? Porque clareza gera ação. E ação gera resultados.

**Você agora é um Planejador Estratégico da Própria Vida.**

Isso significa que você não está mais vagando sem direção. Você tem um destino. Um GPS. E a cada dia, você pode se perguntar: "Isso me aproxima ou me afasta da minha meta?"

Lembre-se: a meta é importante, mas a pessoa que você se torna perseguindo ela é ainda mais importante. Porque no caminho para ${metadata.goalTitle ? `"${metadata.goalTitle}"` : 'sua meta'}, você vai desenvolver disciplina, resiliência, foco. Você vai evoluir.

Continue planejando. Continue executando. E principalmente: continue ajustando. Porque metas não são estáticas - elas evoluem conforme você evolui.

Com orgulho pelo seu planejamento,

**Equipe 90diasapp** 🎯
*"Um objetivo sem um plano é apenas um desejo." - Antoine de Saint-Exupéry*`,

      'badge-habit1': `Incrível, ${userName}! 🔄

Você acabou de dar o passo mais importante para qualquer transformação duradoura: criar seu primeiro hábito.

${metadata.habitTitle ? `"${metadata.habitTitle}"` : 'Seu hábito'} pode parecer pequeno agora. Talvez seja apenas 5 minutos. Talvez seja uma ação simples. Mas não se engane: **pequenas ações consistentes geram resultados extraordinários.**

Sabe qual é a diferença entre pessoas que conquistam grandes transformações e aquelas que ficam apenas sonhando? Hábitos. Disciplina diária. Ações repetidas. É simples, mas não é fácil.

E você acabou de provar que entende isso. Você não está esperando motivação. Você não está esperando o "momento perfeito". Você criou um sistema. Um compromisso. Uma prática diária.

**Você agora é um Construtor de Hábitos.**

E essa é uma identidade poderosa. Porque construtores de hábitos sabem que:
- Transformação não acontece em 1 dia, mas em 90 dias de consistência
- Motivação é temporária, mas sistemas são permanentes
- Você não precisa mudar tudo de uma vez - apenas uma coisa por vez, bem feita

Seu próximo passo? Marcar esse hábito todo dia. Não porque é fácil, mas porque você se comprometeu. E quando você se comprometer com outros hábitos (e vai se comprometer!), você terá a prova de que consegue.

${metadata.habitTitle ? `Cada vez que você fizer "${metadata.habitTitle}"` : 'Cada vez que você praticar seu hábito'}, você está reforçando sua nova identidade: "Eu sou uma pessoa disciplinada que honra compromissos."

E essa identidade? Ela te levará muito mais longe do que qualquer meta isolada.

Continue construindo, tijolo por tijolo.

Com admiração pela sua decisão,

**Equipe 90diasapp** 💪
*"Nós somos o que repetidamente fazemos. A excelência não é um ato, mas um hábito." - Aristóteles*`,

      'badge-hydration': `Muito bem, ${userName}! 💧

Você bebeu ${metadata.amount || 2000}ml de água hoje. E pode parecer simples, mas você acabou de fazer algo que a maioria das pessoas negligencia: cuidar do básico.

**Hidratação não é um luxo. É a base de tudo.**

Seu cérebro é 75% água. Seus músculos são 75% água. Quando você se hidrata adequadamente, você:
- Pensa mais claro (aumento de 14% na performance cognitiva)
- Tem mais energia (fadiga diminui em até 30%)
- Recupera melhor (metabolismo funciona otimamente)
- Tem melhor humor (desidratação causa irritabilidade)

**E sabe o mais importante?** Você não apenas bebeu água. Você PRESTOU ATENÇÃO. Você registrou. Você se comprometeu com sua saúde de forma ativa e consciente.

**Você agora é um Guardião da Própria Saúde.**

Isso pode parecer um exagero para "apenas beber água", mas não é. Porque cuidar do básico é o que separa pessoas que apenas sobrevivem de pessoas que PROSPERAM.

Enquanto outros ignoram os sinais do corpo, você escuta. Enquanto outros negligenciam as necessidades fundamentais, você honra. E essa mentalidade - de cuidado, atenção e compromisso - se expande para todas as áreas da vida.

Continue se hidratando. Continue prestando atenção. Continue honrando seu corpo.

Porque quando você cuida do templo, o templo cuida de você.

Com respeito pela sua consciência,

**Equipe 90diasapp** ⚡
*"Cuide do seu corpo. É o único lugar que você tem para viver." - Jim Rohn*`
    };

    // ✅ FALLBACK: Carta genérica para badges sem carta específica
    return letters[badge.id] || `
Olá, ${userName}! 🎉

**Parabéns por conquistar o badge "${badge.name}"!**

${badge.description}

Esta conquista demonstra seu compromisso com sua evolução pessoal. Continue firme nessa jornada de transformação. 

Cada passo conta, e você está no caminho certo.

**Você é capaz de realizar coisas incríveis!**

Continue evoluindo,

**Equipe 90diasapp** 💪

*"O sucesso é a soma de pequenos esforços repetidos dia após dia."*
    `.trim();
  },

  /**
   * Busca todos os badges do usuário
   */
  async getUserBadges(userId: string) {
    const { data, error } = await supabase
      .from('user_badges')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar badges:', error);
      return [];
    }

    return data || [];
  },

  /**
   * Verifica e concede badges baseado em conquistas do usuário
   */
  async checkAndAwardBadges(userId: string) {
    console.log('🔍 Verificando badges para usuário:', userId);

    // Buscar dados do usuário
    const [
      { data: profile },
      { data: challenges },
      { data: goals },
      { data: habits },
      { data: waterLogs },
    ] = await Promise.all([
      supabase.from('profiles').select('onboarding_completed').eq('id', userId).maybeSingle(),
      supabase.from('user_challenges').select('streak').eq('user_id', userId).order('streak', { ascending: false }).limit(1),
      supabase.from('user_goals').select('id, title').eq('user_id', userId),
      supabase.from('user_habits').select('id, title').eq('user_id', userId).eq('active', true),
      supabase.from('daily_water_logs').select('amount_ml').eq('user_id', userId).gte('amount_ml', 2000).limit(1),
    ]);

    const maxStreak = challenges?.[0]?.streak || 0;
    const hasGoals = (goals?.length || 0) > 0;
    const hasHabits = (habits?.length || 0) > 0;
    const hasHydration = (waterLogs?.length || 0) > 0;

    // Verificar badges de onboarding
    if (profile?.onboarding_completed) {
      await this.awardBadge(userId, 'badge-onboarding');
      await this.awardBadge(userId, 'badge-wheel');
    }

    // Verificar badges de streak
    if (maxStreak >= 7) await this.awardBadge(userId, 'badge-streak7', { streak: maxStreak });
    if (maxStreak >= 21) await this.awardBadge(userId, 'badge-streak21', { streak: maxStreak });
    if (maxStreak >= 66) await this.awardBadge(userId, 'badge-streak66', { streak: maxStreak });
    if (maxStreak >= 90) await this.awardBadge(userId, 'badge-streak90', { streak: maxStreak });

    // Verificar badges de metas e hábitos
    if (hasGoals && goals) {
      await this.awardBadge(userId, 'badge-goal1', { goalTitle: goals[0].title });
    }
    
    if (hasHabits && habits) {
      await this.awardBadge(userId, 'badge-habit1', { habitTitle: habits[0].title });
    }

    // Verificar badge de hidratação
    if (hasHydration && waterLogs) {
      await this.awardBadge(userId, 'badge-hydration', { amount: waterLogs[0].amount_ml });
    }

    console.log('✅ Verificação de badges concluída');
  },
};
